import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { MapContainer, TileLayer, CircleMarker, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import {
  SatelliteIcon, SearchIcon, BatteryWarningIcon, RefreshCwIcon,
  NavigationIcon, PauseCircleIcon, RouteIcon, XIcon, LinkIcon, MapIcon, GlobeIcon,
} from "lucide-react";

/* ============ RASTREAMENTO GPS — GEOFORCE (mapa ao vivo + consulta individual) ============ */

const fmtDT = (s) => (s ? new Date(s).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

/* Direção/rumo (heading Geoforce: graus, 0 = Norte, sentido horário) */
const CARDEAIS = ["N", "NE", "L", "SE", "S", "SO", "O", "NO"];
const headingNum = (a) => {
  const h = parseFloat(a?.heading);
  return Number.isFinite(h) ? ((h % 360) + 360) % 360 : null;
};
const cardeal = (h) => CARDEAIS[Math.round(h / 45) % 8];

/* Pino ↓ cravado na posição atual + seta girada indicando a direção do último movimento */
const pinIcon = (a, cor) => {
  const h = headingNum(a);
  const comSeta = h !== null && !a.stopped;
  return L.divIcon({
    className: "",
    iconSize: [46, 66],
    iconAnchor: [23, 64],
    html: `
      <div style="position:relative;width:46px;height:66px;pointer-events:none;">
        ${comSeta ? `
        <svg width="22" height="22" viewBox="0 0 20 20" style="position:absolute;top:0;left:12px;transform:rotate(${h}deg);filter:drop-shadow(0 1px 2px rgba(0,0,0,.8));">
          <path d="M10 1 L16.5 16 L10 11.5 L3.5 16 Z" fill="#00E5FF" stroke="#0A0A0C" stroke-width="1.2"/>
        </svg>` : ""}
        <svg width="30" height="42" viewBox="0 0 30 42" style="position:absolute;top:22px;left:8px;filter:drop-shadow(0 2px 3px rgba(0,0,0,.65));">
          <path d="M15 42 C15 42 2 24 2 14 A13 13 0 1 1 28 14 C28 24 15 42 15 42Z" fill="${cor}" stroke="#0A0A0C" stroke-width="2"/>
          <circle cx="15" cy="14" r="5" fill="#0A0A0C"/>
        </svg>
      </div>`,
  });
};

function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo([target.lat, target.lng], 12, { duration: 0.8 });
  }, [target, map]);
  return null;
}

function FitTrail({ pontos }) {
  const map = useMap();
  useEffect(() => {
    if (pontos && pontos.length > 1) {
      map.fitBounds(pontos.map((p) => [p.lat, p.lng]), { padding: [40, 40] });
    }
  }, [pontos, map]);
  return null;
}

export default function RastreamentoGPS() {
  const [assets, setAssets] = useState([]);
  const [lastSync, setLastSync] = useState(null);
  const [syncError, setSyncError] = useState(null);
  const [busca, setBusca] = useState("");
  const [sel, setSel] = useState(null);          // ativo selecionado
  const [trilha, setTrilha] = useState(null);    // {esn, pontos}
  const [carregandoTrilha, setCarregandoTrilha] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mapaTipo, setMapaTipo] = useState("satelite"); // satelite | claro
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/gps/assets");
      setAssets(data.assets);
      setLastSync(data.last_sync);
      setSyncError(data.sync_error);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 60000);
    return () => clearInterval(timerRef.current);
  }, []);

  const filtrados = useMemo(() => {
    const s = busca.toLowerCase().trim();
    if (!s) return assets;
    return assets.filter((a) =>
      (a.name || "").toLowerCase().includes(s) ||
      (a.number || "").toLowerCase().includes(s) ||
      (a.account || "").toLowerCase().includes(s) ||
      (a.esn || "").toLowerCase().includes(s)
    );
  }, [assets, busca]);

  const kpis = useMemo(() => ({
    total: assets.length,
    movimento: assets.filter((a) => !a.stopped).length,
    parados: assets.filter((a) => a.stopped).length,
    bateria: assets.filter((a) => a.battery_ok === false).length,
  }), [assets]);

  const verTrilha = async (a, horas) => {
    setCarregandoTrilha(true);
    try {
      const { data } = await api.get(`/gps/assets/${encodeURIComponent(a.esn)}/historico`, { params: { horas } });
      if (!data.pontos.length) { toast.info("Sem leituras no período — o ativo pode reportar em intervalos longos"); setTrilha(null); }
      else { setTrilha({ esn: a.esn, pontos: data.pontos, horas }); toast.success(`${data.total} posições nas últimas ${horas}h`); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCarregandoTrilha(false); }
  };

  const selecionar = (a) => {
    setSel(a);
    setTrilha(null);
  };

  const corMarker = (a) => (a.battery_ok === false ? "#FF3B30" : a.stopped ? "#FFD60A" : "#34C759");

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="rastreamento-gps-page">
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#00E5FF] mb-1 flex items-center gap-2">
            <SatelliteIcon className="w-4 h-4" /> Telemetria Geoforce · Tempo Real
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-none">Rastreamento <span className="text-[#FFD60A]">GPS</span></h1>
        </div>
        <div className="ml-auto flex items-center gap-3 flex-wrap">
          <div className="text-[10px] font-mono text-muted-foreground" data-testid="gps-last-sync">
            Última sync: {fmtDT(lastSync)}
          </div>
          <Button size="sm" variant="outline" onClick={load} data-testid="gps-refresh" className="rounded-none border-border h-8 text-[10px] font-bold uppercase">
            <RefreshCwIcon className="w-3 h-3 mr-1" /> Atualizar
          </Button>
        </div>
      </div>

      {syncError && (
        <div className="border border-[#FF9F0A]/40 bg-[#FF9F0A]/5 px-4 py-2 text-xs text-[#FF9F0A]" data-testid="gps-sync-error">
          Aviso: sincronização Geoforce falhou ({syncError}) — exibindo últimas posições em cache.
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ["Ativos Rastreados", kpis.total, "#00E5FF", SatelliteIcon],
          ["Em Movimento", kpis.movimento, "#34C759", NavigationIcon],
          ["Parados", kpis.parados, "#FFD60A", PauseCircleIcon],
          ["Alerta de Bateria", kpis.bateria, "#FF3B30", BatteryWarningIcon],
        ].map(([label, val, cor, Icon]) => (
          <Card key={label} className="bg-[#14151A] border-border rounded-none p-3 flex items-center gap-3" data-testid={`gps-kpi-${label}`}>
            <Icon className="w-5 h-5" style={{ color: cor }} />
            <div>
              <div className="font-mono text-2xl font-black leading-none" style={{ color: cor }}>{val.toLocaleString("pt-BR")}</div>
              <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
            </div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[380px_1fr] gap-4">
        {/* Sidebar: busca + lista */}
        <Card className="bg-[#14151A] border-border rounded-none overflow-hidden flex flex-col max-h-[640px]">
          <div className="p-3 border-b border-border">
            <div className="relative">
              <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={busca} onChange={(e) => setBusca(e.target.value)} data-testid="gps-busca"
                     className="rounded-none bg-[#0A0A0C] border-border h-10 pl-9 font-mono text-sm"
                     placeholder="Buscar contentor, nº, empresa..." />
            </div>
            <div className="text-[10px] text-muted-foreground mt-1 font-mono">{filtrados.length.toLocaleString("pt-BR")} resultado(s)</div>
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-border/40" data-testid="gps-lista">
            {loading && <div className="p-6 text-center text-xs text-muted-foreground animate-pulse">Carregando ativos...</div>}
            {!loading && filtrados.slice(0, 300).map((a) => (
              <button key={a.esn} onClick={() => selecionar(a)} data-testid={`gps-item-${a.esn}`}
                      className={`w-full text-left p-3 hover:bg-[#0A0A0C] transition-colors ${sel?.esn === a.esn ? "bg-[#0A0A0C] border-l-2 border-[#FFD60A]" : ""}`}>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: corMarker(a) }} />
                  <span className="text-xs font-bold truncate">{a.name}</span>
                </div>
                <div className="text-[10px] font-mono text-muted-foreground mt-0.5 flex flex-wrap gap-x-3">
                  <span>{a.number || a.esn}</span>
                  <span>{a.account || ""}</span>
                  <span>{fmtDT(a.timestamp)}</span>
                </div>
                {a.wms && (
                  <Badge className="rounded-none bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/40 text-[8px] font-bold mt-1">
                    WMS · {a.wms.status?.replace(/_/g, " ")}
                  </Badge>
                )}
              </button>
            ))}
            {!loading && filtrados.length > 300 && (
              <div className="p-3 text-center text-[10px] text-muted-foreground">Mostrando 300 de {filtrados.length} — refine a busca</div>
            )}
            {!loading && !filtrados.length && <div className="p-6 text-center text-xs text-muted-foreground">Nenhum ativo encontrado.</div>}
          </div>

          {/* Painel do ativo selecionado */}
          {sel && (
            <div className="border-t-2 border-[#FFD60A] bg-[#0A0A0C] p-3 space-y-2" data-testid="gps-detalhe">
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <div className="text-sm font-black">{sel.name}</div>
                  <div className="text-[10px] font-mono text-muted-foreground">Nº {sel.number || "—"} · ESN {sel.esn} · {sel.account || "—"}</div>
                </div>
                <button onClick={() => { setSel(null); setTrilha(null); }} data-testid="gps-fechar-detalhe" className="text-muted-foreground hover:text-white"><XIcon className="w-4 h-4" /></button>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[10px] font-mono">
                <div>Status: <b style={{ color: corMarker(sel) }}>{sel.stopped ? "PARADO" : "EM MOVIMENTO"}</b></div>
                <div>Velocidade: <b>{sel.speed || "0"} km/h</b></div>
                <div data-testid="gps-direcao">Direção: {!sel.stopped && headingNum(sel) !== null ? (
                  <b className="text-[#00E5FF]">
                    <span style={{ display: "inline-block", transform: `rotate(${headingNum(sel)}deg)` }}>↑</span> {cardeal(headingNum(sel))} ({Math.round(headingNum(sel))}°)
                  </b>
                ) : <b>— {sel.stopped ? "(parado)" : ""}</b>}</div>
                <div>Bateria: <b className={sel.battery_ok === false ? "text-[#FF3B30]" : "text-[#34C759]"}>{sel.battery_ok === false ? "ALERTA" : "OK"}</b></div>
                <div>Reportado: <b>{fmtDT(sel.timestamp)}</b></div>
                <div className="col-span-2">Posição: <b>{sel.lat?.toFixed(5)}, {sel.lng?.toFixed(5)}</b></div>
              </div>
              {sel.wms && (
                <div className="border border-[#00E5FF]/30 bg-[#00E5FF]/5 p-2 text-[10px]" data-testid="gps-wms-info">
                  <div className="text-[#00E5FF] font-bold uppercase tracking-wider mb-0.5 flex items-center gap-1"><LinkIcon className="w-3 h-3" /> Vínculo WMS</div>
                  <div className="font-mono">{sel.wms.no_serie} · {sel.wms.status?.replace(/_/g, " ")}</div>
                  {sel.wms.nome_produto && <div>{sel.wms.nome_produto} · Lote {sel.wms.no_lote || "—"}</div>}
                  {sel.wms.cliente && <div>Cliente: {sel.wms.cliente}</div>}
                </div>
              )}
              <div className="flex gap-2">
                <Button size="sm" disabled={carregandoTrilha} onClick={() => verTrilha(sel, 24)} data-testid="gps-trilha-24h"
                        className="rounded-none bg-[#FFD60A] text-black hover:bg-[#ffdf3a] h-8 text-[9px] font-bold uppercase flex-1">
                  <RouteIcon className="w-3 h-3 mr-1" /> Trilha 24h
                </Button>
                <Button size="sm" disabled={carregandoTrilha} onClick={() => verTrilha(sel, 168)} data-testid="gps-trilha-7d"
                        variant="outline" className="rounded-none border-[#FFD60A] text-[#FFD60A] hover:bg-[#FFD60A]/10 h-8 text-[9px] font-bold uppercase flex-1">
                  <RouteIcon className="w-3 h-3 mr-1" /> Trilha 7 dias
                </Button>
              </div>
              {trilha && (
                <div className="text-[9px] font-mono text-[#00E5FF]">{trilha.pontos.length} posições nas últimas {trilha.horas}h — trilha no mapa</div>
              )}
            </div>
          )}
        </Card>

        {/* Mapa */}
        <Card className="bg-[#14151A] border-border rounded-none overflow-hidden relative">
          {/* Alternador de camada */}
          <div className="absolute top-3 right-3 z-[500] flex border border-border bg-[#14151A] shadow-lg">
            <button onClick={() => setMapaTipo("satelite")} data-testid="gps-mapa-satelite"
                    className={`px-3 h-9 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 ${mapaTipo === "satelite" ? "bg-[#FFD60A] text-black" : "text-muted-foreground hover:text-white"}`}>
              <GlobeIcon className="w-3.5 h-3.5" /> Satélite
            </button>
            <button onClick={() => setMapaTipo("claro")} data-testid="gps-mapa-claro"
                    className={`px-3 h-9 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 ${mapaTipo === "claro" ? "bg-[#FFD60A] text-black" : "text-muted-foreground hover:text-white"}`}>
              <MapIcon className="w-3.5 h-3.5" /> Claro
            </button>
          </div>
          <div className="h-[640px] w-full">
            <MapContainer center={[-22.9, -41.5]} zoom={7} preferCanvas={true} style={{ height: "100%", width: "100%" }} data-testid="gps-mapa">
              {mapaTipo === "satelite" ? (
                <>
                  <TileLayer
                    key="sat"
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                    attribution='&copy; Esri &copy; Maxar, Earthstar Geographics'
                  />
                  <TileLayer
                    key="sat-labels"
                    url="https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png"
                    attribution='&copy; OpenStreetMap &copy; CARTO'
                  />
                </>
              ) : (
                <TileLayer
                  key="claro"
                  url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                  attribution='&copy; OpenStreetMap &copy; CARTO'
                />
              )}
              <FlyTo target={sel} />
              {trilha && <FitTrail pontos={trilha.pontos} />}
              {/* Pino ↓ + seta de direção do ativo selecionado */}
              {sel && (() => {
                const atual = filtrados.find((x) => x.esn === sel.esn) || sel;
                return (
                  <Marker
                    position={[atual.lat, atual.lng]}
                    icon={pinIcon(atual, corMarker(atual))}
                    interactive={false}
                    zIndexOffset={1000}
                  />
                );
              })()}
              {filtrados.map((a) => (
                <CircleMarker
                  key={a.esn}
                  center={[a.lat, a.lng]}
                  radius={sel?.esn === a.esn ? 10 : 6}
                  eventHandlers={{ click: () => selecionar(a) }}
                  pathOptions={{ color: mapaTipo === "claro" ? "#1F2937" : corMarker(a), fillColor: corMarker(a), fillOpacity: 0.85, weight: sel?.esn === a.esn ? 3 : 1.5 }}
                >
                  <Popup>
                    <div className="text-xs">
                      <div className="font-mono font-bold">{a.name}</div>
                      <div className="text-muted-foreground">Nº {a.number || a.esn} · {a.account || ""}</div>
                      <div>{a.stopped ? "Parado" : "Em movimento"} · {fmtDT(a.timestamp)}</div>
                      {!a.stopped && headingNum(a) !== null && (
                        <div>Direção: <span style={{ display: "inline-block", transform: `rotate(${headingNum(a)}deg)` }}>↑</span> {cardeal(headingNum(a))} ({Math.round(headingNum(a))}°)</div>
                      )}
                      {a.wms && <div className="mt-1">WMS: {a.wms.status?.replace(/_/g, " ")} {a.wms.cliente ? `· ${a.wms.cliente}` : ""}</div>}
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
              {trilha && (
                <>
                  <Polyline positions={trilha.pontos.map((p) => [p.lat, p.lng])} pathOptions={{ color: "#00E5FF", weight: 3, opacity: 0.85 }} />
                  {trilha.pontos.map((p, i) => (
                    <CircleMarker key={i} center={[p.lat, p.lng]} radius={3} pathOptions={{ color: "#00E5FF", fillColor: "#00E5FF", fillOpacity: 1 }}>
                      <Popup><div className="text-xs font-mono">{fmtDT(p.timestamp)}<br />{p.speed || 0} km/h</div></Popup>
                    </CircleMarker>
                  ))}
                </>
              )}
            </MapContainer>
          </div>
        </Card>
      </div>
    </div>
  );
}
